import { Component, inject, input, output, signal } from '@angular/core';
import { SnackbarService } from '../../../../../core/services';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { AppTheme, ColorMode, ThemeService } from '../../../../../core/services/theme.service';
import { BRANDING } from '../../../../../core/config/branding';

type DeviceOption = {
  deviceId: string;
  label: string;
};

const AUDIO_INPUT_STORAGE_KEY = 'meetrix.daily.audioInputId';
const AUDIO_OUTPUT_STORAGE_KEY = 'meetrix.daily.audioOutputId';
const VIDEO_INPUT_STORAGE_KEY = 'meetrix.daily.videoInputId';

@Component({
  selector: 'app-settings-screen',
  imports: [MatCardModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './settings-screen.component.html',
  styleUrl: './settings-screen.component.scss'
})
export class SettingsScreenComponent {
  protected readonly brand = BRANDING;
  private readonly themeService = inject(ThemeService);
  private readonly snackbar = inject(SnackbarService);
  protected readonly notificationPermission = signal<NotificationPermission | 'unsupported'>(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  );
  protected readonly microphoneDevices = signal<DeviceOption[]>([]);
  protected readonly speakerDevices = signal<DeviceOption[]>([]);
  protected readonly cameraDevices = signal<DeviceOption[]>([]);
  protected readonly selectedMicrophoneId = signal<string>('');
  protected readonly selectedSpeakerId = signal<string>('');
  protected readonly selectedCameraId = signal<string>('');
  protected readonly mediaDevicesSupported = signal(
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.enumerateDevices
  );
  protected readonly audioOutputSupported = signal(
    typeof window !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
  );

  readonly userName = input<string>('User');
  readonly userEmail = input<string>('');
  readonly userPhotoUrl = input<string | null>(null);
  readonly canManageUsers = input<boolean>(false);
  readonly canManageCompany = input<boolean>(false);
  readonly chatSoundsEnabled = input<boolean>(true);
  readonly chatSoundVolume = input<number>(14);

  protected getUserInitials(): string {
    const name = this.userName().trim();
    if (!name) {
      return 'U';
    }

    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  readonly openProfile = output<void>();
  readonly openChangePassword = output<void>();
  readonly openManagement = output<void>();
  readonly toggleChatSounds = output<void>();
  readonly chatSoundVolumeChange = output<number>();

  protected readonly currentTheme = this.themeService.currentTheme;
  protected readonly themeOptions = this.themeService.themeOptions;
  protected readonly currentColorMode = this.themeService.currentColorMode;
  protected readonly colorModeOptions = this.themeService.colorModeOptions;

  protected onOpenProfile(): void {
    this.openProfile.emit();
  }

  protected onOpenChangePassword(): void {
    this.openChangePassword.emit();
  }

  protected onOpenManagement(): void {
    this.openManagement.emit();
  }

  protected onToggleChatSounds(): void {
    this.toggleChatSounds.emit();
  }

  protected onChatSoundVolumeChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = Number(target?.value ?? this.chatSoundVolume());

    if (!Number.isFinite(nextValue)) {
      return;
    }

    this.chatSoundVolumeChange.emit(Math.min(100, Math.max(0, Math.round(nextValue))));
  }

  protected onThemeChange(theme: AppTheme): void {
    this.themeService.setTheme(theme);
  }

  protected onColorModeChange(mode: ColorMode): void {
    this.themeService.setColorMode(mode);
  }

  ngOnInit(): void {
    this.loadStoredDevicePreferences();
    void this.refreshMediaDevices();
  }

  protected canRequestNotifications(): boolean {
    return this.notificationPermission() === 'default';
  }

  protected notificationsEnabled(): boolean {
    return this.notificationPermission() === 'granted';
  }

  protected notificationsUnsupported(): boolean {
    return this.notificationPermission() === 'unsupported';
  }

  protected requestNotifications(): void {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      this.notificationPermission.set('unsupported');
      return;
    }

    if (this.notificationPermission() === 'denied') {
      this.snackbar.showNotification(
        'warning',
        '',
        'Notifications are blocked. Use the browser site settings to allow them.',
        'Close'
      );
      return;
    }

    Notification.requestPermission()
      .then((permission) => this.notificationPermission.set(permission))
      .catch(() => {});
  }
  
  protected async enableDeviceAccess(): Promise<void> {
    if (!this.mediaDevicesSupported()) {
      this.snackbar.showNotification('warning', '', 'Media device selection is not supported in this browser.', 'Close');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach((track) => track.stop());
      await this.refreshMediaDevices();
      this.snackbar.showNotification('success', '', 'Devices detected successfully.', 'Close');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to access camera or microphone.';
      this.snackbar.showNotification('warning', '', message, 'Close');
    }
  }

  protected onMicrophoneChange(deviceId: string): void {
    this.selectedMicrophoneId.set(deviceId);
    this.persistDevicePreference(AUDIO_INPUT_STORAGE_KEY, deviceId);
  }

  protected onSpeakerChange(deviceId: string): void {
    this.selectedSpeakerId.set(deviceId);
    this.persistDevicePreference(AUDIO_OUTPUT_STORAGE_KEY, deviceId);
  }

  protected onCameraChange(deviceId: string): void {
    this.selectedCameraId.set(deviceId);
    this.persistDevicePreference(VIDEO_INPUT_STORAGE_KEY, deviceId);
  }

  private async refreshMediaDevices(): Promise<void> {
    if (!this.mediaDevicesSupported()) {
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const microphones = devices
      .filter((device) => device.kind === 'audioinput')
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${index + 1}`,
      }));
    const speakers = devices
      .filter((device) => device.kind === 'audiooutput')
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Speaker ${index + 1}`,
      }));
    const cameras = devices
      .filter((device) => device.kind === 'videoinput')
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
      }));

    this.microphoneDevices.set(microphones);
    this.speakerDevices.set(speakers);
    this.cameraDevices.set(cameras);

    this.selectedMicrophoneId.set(this.resolveSelectedDeviceId(microphones, this.selectedMicrophoneId()));
    this.selectedSpeakerId.set(this.resolveSelectedDeviceId(speakers, this.selectedSpeakerId()));
    this.selectedCameraId.set(this.resolveSelectedDeviceId(cameras, this.selectedCameraId()));

    this.persistDevicePreference(AUDIO_INPUT_STORAGE_KEY, this.selectedMicrophoneId());
    this.persistDevicePreference(AUDIO_OUTPUT_STORAGE_KEY, this.selectedSpeakerId());
    this.persistDevicePreference(VIDEO_INPUT_STORAGE_KEY, this.selectedCameraId());
  }

  private resolveSelectedDeviceId(options: DeviceOption[], selectedDeviceId: string): string {
    if (!options.length) {
      return '';
    }

    const existing = options.find((option) => option.deviceId === selectedDeviceId);
    if (existing) {
      return existing.deviceId;
    }

    return options[0].deviceId;
  }

  private loadStoredDevicePreferences(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.selectedMicrophoneId.set(window.localStorage.getItem(AUDIO_INPUT_STORAGE_KEY) ?? '');
    this.selectedSpeakerId.set(window.localStorage.getItem(AUDIO_OUTPUT_STORAGE_KEY) ?? '');
    this.selectedCameraId.set(window.localStorage.getItem(VIDEO_INPUT_STORAGE_KEY) ?? '');
  }

  private persistDevicePreference(storageKey: string, deviceId: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!deviceId) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, deviceId);
  }
}
